import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, X, UserRound, CheckCircle2, Check, Clock, EyeOff } from "lucide-react";

const STATUS_LABEL = { open: "OPEN", in_progress: "IN PROGRESS", resolved: "RESOLVED" };

// Tiny external store — the sidebar remounts on every layout render, so the
// bell button must stay dumb; data + panel live in a stable host component.
let _state = { data: null, open: false };
let _listeners = [];
let _nudged = false;
const setBellState = (patch) => {
  _state = { ..._state, ...patch };
  _listeners.forEach((l) => l(_state));
};
const useBellState = () => {
  const [s, setS] = useState(_state);
  useEffect(() => {
    _listeners.push(setS);
    setS(_state);
    return () => { _listeners = _listeners.filter((l) => l !== setS); };
  }, []);
  return s;
};
const loadBell = () => api.getMyActions().then((r) => {
  setBellState({ data: r.data });
  const od = (r.data.mine || []).filter((m) => m.overdue).length;
  if (od > 0 && !_nudged) {
    _nudged = true;
    toast.warning(`You have ${od} overdue action${od > 1 ? "s" : ""} — check the bell`, { duration: 8000 });
  }
}).catch(() => {});

// Sidebar button — badge only, safe to remount
export const ActionBell = ({ collapsed }) => {
  const s = useBellState();
  const mine = s.data?.mine || [];
  const others = s.data?.others_overdue || [];
  const overdueCount = mine.filter((m) => m.overdue).length + others.length;
  const qg = s.data?.qty_guard;
  const totalCount = mine.length + others.length + (qg?.high || 0);
  return (
    <button onClick={() => { setBellState({ open: true }); loadBell(); }} data-testid="action-bell"
      className="relative flex items-center gap-2.5 px-2.5 py-2 rounded-sm text-sm font-medium text-[#6B7280] hover:text-[#1A1D21] hover:bg-[#F1F3F5] w-full">
      <span className="relative shrink-0">
        <Bell size={16} strokeWidth={1.5} />
        {totalCount > 0 && (
          <span data-testid="action-bell-badge"
            className={`absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-0.5 rounded-full text-[8px] font-bold text-white flex items-center justify-center ${overdueCount > 0 ? "bg-red-600" : "bg-amber-500"}`}>
            {overdueCount > 0 ? overdueCount : totalCount}
          </span>
        )}
      </span>
      {!collapsed && <span>My Actions</span>}
    </button>
  );
};

// Stable host — render ONCE at the layout root (owns fetching + the panel)
export const ActionBellHost = () => {
  const s = useBellState();
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    loadBell();
    const t = setInterval(loadBell, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const mine = s.data?.mine || [];
  const others = s.data?.others_overdue || [];
  const overdueCount = mine.filter((m) => m.overdue).length + others.length;

  const update = async (id, payload, msg) => {
    try {
      await api.updateMyAction(id, payload);
      if (msg) toast.success(msg);
      loadBell();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const dismissQty = async (ids, label) => {
    try {
      await api.dismissQtyExceptions(ids, "Dismissed from My Actions");
      toast.success(label);
      loadBell();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not dismiss"); }
  };
  const addNote = async (id) => {
    if (!noteText.trim()) return;
    await update(id, { add_note: noteText }, "Action noted");
    setNoteFor(null);
    setNoteText("");
  };

  if (!s.open) return null;
  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/20" onClick={() => setBellState({ open: false })} />
      <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[400px] bg-white border-l border-[#E2E5EA] shadow-2xl overflow-y-auto" data-testid="action-bell-panel">
        <div className="sticky top-0 bg-[#1A1D21] text-white px-4 py-3 flex items-center gap-2 z-10">
          <Bell size={14} className={overdueCount > 0 ? "text-red-400" : "text-amber-400"} />
          <span className="text-xs font-bold uppercase tracking-wider">My Actions</span>
          <span className="text-[10px] text-gray-400">{mine.length} assigned{overdueCount ? ` · ${overdueCount} overdue` : ""}</span>
          <button onClick={() => setBellState({ open: false })} className="ml-auto text-gray-400 hover:text-white" data-testid="action-bell-close"><X size={16} /></button>
        </div>

        <div className="p-3 space-y-2">
          {s.data?.qty_guard && s.data.qty_guard.open > 0 && (
            <div className={`border rounded-sm p-2.5 ${s.data.qty_guard.high ? "border-red-300 bg-red-50/50" : "border-amber-300 bg-amber-50/50"}`} data-testid="my-action-qty-guard">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs font-semibold flex-1">⚖ Quantity exceptions — {s.data.qty_guard.open} open{s.data.qty_guard.high ? `, ${s.data.qty_guard.high} high` : ""}</span>
                <button onClick={() => { setBellState({ open: false }); navigate("/reconciliation?tab=qty"); }} className="text-[9px] font-bold text-[#E32C17] underline" data-testid="my-action-qty-guard-open">Review</button>
                <button onClick={() => {
                  const ids = (s.data.qty_guard.top || []).map((t) => t.id);
                  if (window.confirm(`Dismiss the ${ids.length} exception(s) listed here? They stay dismissed until someone re-opens them on the Reconciliation screen.`)) {
                    dismissQty(ids, `${ids.length} dismissed`);
                  }
                }}
                  className="text-[9px] font-bold text-[#6B7280] underline flex items-center gap-0.5" data-testid="my-action-qty-guard-dismiss">
                  <EyeOff size={9} /> Dismiss listed ({(s.data.qty_guard.top || []).length})
                </button>
              </div>
              <ul className="mt-1 space-y-0.5">
                {(s.data.qty_guard.top || []).map((t) => (
                  <li key={t.id} className="flex items-start gap-1 text-[10px] text-[#6B7280] group">
                    <span className="flex-1 truncate" title={t.message}>{t.severity === "high" ? "● " : "○ "}{t.message}</span>
                    <button onClick={() => dismissQty([t.id], "Dismissed")} title="Dismiss this one"
                      className="shrink-0 text-[#C9CDD3] hover:text-red-600" data-testid={`qty-dismiss-${t.id}`}>
                      <X size={10} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {mine.length === 0 && !s.data?.qty_guard?.open && <div className="text-center text-xs text-[#9CA3AF] py-6">Nothing assigned to you right now — all clear.</div>}
          {mine.map((i) => (
            <div key={i.id} className={`border rounded-sm p-2.5 ${i.overdue ? "border-red-300 bg-red-50/50" : "border-[#E2E5EA]"}`} data-testid={`my-action-${i.id}`}>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs font-semibold flex-1">{i.title}</span>
                {i.overdue && <span className="text-[8px] px-1.5 py-0.5 bg-red-600 text-white rounded-sm font-bold uppercase">Overdue</span>}
              </div>
              {i.detail && <p className="text-[10px] text-[#6B7280] mt-1">{i.detail}</p>}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {i.due_date && <span className={`text-[10px] font-mono ${i.overdue ? "text-red-600 font-bold" : "text-[#9CA3AF]"}`}>due {i.due_date}</span>}
                <select value={i.status} onChange={(e) => update(i.id, { status: e.target.value }, `Marked ${STATUS_LABEL[e.target.value]}`)}
                  className="text-[9px] border border-[#E2E5EA] rounded-sm px-1.5 py-1 font-bold uppercase bg-white" data-testid={`my-action-status-${i.id}`}>
                  {["open", "in_progress", "resolved"].map((st) => <option key={st} value={st}>{STATUS_LABEL[st]}</option>)}
                </select>
                {noteFor !== i.id && (
                  <button onClick={() => { setNoteFor(i.id); setNoteText(""); }} className="text-[9px] px-2 py-1 bg-white border border-[#E2E5EA] rounded-sm font-bold text-[#6B7280]" data-testid={`my-action-note-btn-${i.id}`}>+ Action taken</button>
                )}
                <button onClick={() => update(i.id, { status: "resolved" }, "Done — cleared from your list")}
                  className="text-[9px] px-2 py-1 bg-emerald-600 text-white rounded-sm font-bold flex items-center gap-1" data-testid={`my-action-done-${i.id}`}>
                  <Check size={9} /> Done
                </button>
                <button onClick={() => update(i.id, { snooze_days: 7 }, "Snoozed for a week")}
                  className="text-[9px] px-2 py-1 bg-white border border-[#E2E5EA] rounded-sm font-bold text-[#6B7280] flex items-center gap-1" data-testid={`my-action-snooze-${i.id}`}>
                  <Clock size={9} /> Snooze 7d
                </button>
              </div>
              {noteFor === i.id && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  <input autoFocus value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote(i.id)}
                    placeholder="What did you do about it?" className="text-[11px] border border-[#E2E5EA] rounded-sm px-2 py-1 flex-1" data-testid={`my-action-note-input-${i.id}`} />
                  <button onClick={() => addNote(i.id)} className="text-[9px] px-2 py-1 bg-[#1F2937] text-white rounded-sm font-bold" data-testid={`my-action-note-save-${i.id}`}>Add</button>
                </div>
              )}
              {(i.notes || []).length > 0 && (
                <div className="mt-1.5 space-y-0.5">
                  {(i.notes || []).slice(-2).map((n, idx) => (
                    <div key={idx} className="flex items-start gap-1.5 text-[10px] text-[#6B7280]">
                      <CheckCircle2 size={10} className="text-emerald-600 mt-0.5 shrink-0" />
                      <span className="flex-1">{n.note}</span>
                      <span className="text-[8px] text-[#9CA3AF] font-mono shrink-0">{(n.at || "").slice(5, 10)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {s.data?.is_director && others.length > 0 && (
            <div className="pt-2" data-testid="others-overdue-section">
              <div className="text-[9px] font-bold uppercase text-red-700 mb-1.5">Overdue with others — who's sitting on what</div>
              {others.map((i) => (
                <div key={i.id} onClick={() => { setBellState({ open: false }); navigate("/directors"); }}
                  className="border border-red-200 bg-red-50/40 rounded-sm p-2 mb-1.5 cursor-pointer hover:bg-red-50" data-testid={`other-overdue-${i.id}`}>
                  <div className="text-xs font-semibold">{i.title}</div>
                  <div className="flex items-center gap-2 text-[10px] mt-0.5">
                    <span className="flex items-center gap-1 font-bold"><UserRound size={10} />{(i.assigned_to || "unassigned").split("@")[0]}</span>
                    <span className="font-mono text-red-600 font-bold">due {i.due_date}</span>
                    <span className="text-[#9CA3AF] ml-auto">open in Directors →</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
